import { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { supabase } from "../lib/supabaseClient";

const colors = {
    paper: "rgba(255, 250, 242, 0.94)",
    ink: "#2a2621",
    muted: "rgba(42, 38, 33, 0.66)",
    brown: "#9b7653",
    brownDark: "#6f5136",
    brownDarker: "#543b26",
    border: "rgba(155,118,83,0.16)",
};

const defaultOpeningHours = [
    { day: "Montag", time: "Ruhetag" },
    { day: "Dienstag", time: "11:00 – 17:00 Uhr" },
    { day: "Mittwoch", time: "11:00 – 17:00 Uhr" },
    { day: "Donnerstag", time: "11:00 – 17:00 Uhr" },
    { day: "Freitag", time: "11:00 – 17:00 Uhr" },
    { day: "Samstag", time: "11:00 – 17:00 Uhr" },
    { day: "Sonntag", time: "11:00 – 17:00 Uhr" },
];

const defaultForm = {
    eyebrow: "Kontakt",
    headline: "Wir freuen uns auf Sie.",
    intro:
        "Besuchen Sie uns direkt im Herzen von Hooksiel oder melden Sie sich telefonisch oder per E-Mail.",
    business_name: "Hooksieler Lädchen",
    contact_person: "Claudia Don",
    street: "Lange Straße 37",
    postal_city: "26434 Hooksiel",
    phone_label: "04425 – 353",
    phone_href: "+494425353",
    fax: "04425 – 6759821",
    email: "info@hooksieler-laedchen.de",
    image_url: "",
    image_alt: "Claudia Don im Hooksieler Lädchen",
    image_badge_label: "Persönlich vor Ort",
    image_badge_text: "Claudia Don freut sich auf Ihren Besuch.",
    opening_hours: defaultOpeningHours,
};

function cleanText(value) {
    return typeof value === "string" ? value.trim() : "";
}

function cleanNullableText(value) {
    const cleanedValue = cleanText(value);
    return cleanedValue || null;
}

function normalizeOpeningHours(value) {
    if (!Array.isArray(value) || value.length === 0) {
        return defaultOpeningHours;
    }

    return defaultOpeningHours.map((defaultItem) => {
        const existingItem = value.find((item) => item.day === defaultItem.day);

        return {
            day: defaultItem.day,
            time:
                typeof existingItem?.time === "string"
                    ? existingItem.time
                    : defaultItem.time,
        };
    });
}

function AdminContactSettings() {
    const [form, setForm] = useState(defaultForm);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        async function loadSettings() {
            try {
                setIsLoading(true);
                setSuccessMessage("");
                setErrorMessage("");

                const { data, error } = await supabase
                    .from("contact_settings")
                    .select("*")
                    .eq("id", "main")
                    .maybeSingle();

                if (error) throw error;

                if (!data) {
                    setForm(defaultForm);
                    setErrorMessage(
                        "Es wurden noch keine Kontaktdaten gefunden. Bitte einmal speichern, um sie anzulegen."
                    );
                    return;
                }

                setForm({
                    ...defaultForm,
                    ...data,
                    eyebrow: data.eyebrow || defaultForm.eyebrow,
                    headline: data.headline || defaultForm.headline,
                    intro: data.intro || defaultForm.intro,
                    business_name:
                        data.business_name || defaultForm.business_name,
                    contact_person:
                        data.contact_person || defaultForm.contact_person,
                    street: data.street || defaultForm.street,
                    postal_city: data.postal_city || defaultForm.postal_city,
                    phone_label: data.phone_label || defaultForm.phone_label,
                    phone_href: data.phone_href || defaultForm.phone_href,
                    fax: data.fax || "",
                    email: data.email || defaultForm.email,
                    image_url: data.image_url || "",
                    image_alt: data.image_alt || defaultForm.image_alt,
                    image_badge_label:
                        data.image_badge_label ||
                        defaultForm.image_badge_label,
                    image_badge_text:
                        data.image_badge_text ||
                        defaultForm.image_badge_text,
                    opening_hours: normalizeOpeningHours(data.opening_hours),
                });
            } catch (error) {
                console.error("Contact settings load error:", error);
                setErrorMessage(
                    error.message || "Kontaktdaten konnten nicht geladen werden."
                );
            } finally {
                setIsLoading(false);
            }
        }

        loadSettings();
    }, []);

    function updateField(name, value) {
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    function updateOpeningHour(index, value) {
        setForm((prev) => {
            const nextOpeningHours = [...prev.opening_hours];

            nextOpeningHours[index] = {
                ...nextOpeningHours[index],
                time: value,
            };

            return {
                ...prev,
                opening_hours: nextOpeningHours,
            };
        });
    }

    async function handleSubmit(event) {
        event.preventDefault();

        try {
            setIsSubmitting(true);
            setSuccessMessage("");
            setErrorMessage("");

            const payload = {
                id: "main",
                eyebrow: cleanText(form.eyebrow),
                headline: cleanText(form.headline),
                intro: cleanText(form.intro),
                business_name: cleanText(form.business_name),
                contact_person: cleanText(form.contact_person),
                street: cleanText(form.street),
                postal_city: cleanText(form.postal_city),
                phone_label: cleanText(form.phone_label),
                phone_href: cleanText(form.phone_href),
                fax: cleanNullableText(form.fax),
                email: cleanText(form.email),
                image_url: cleanNullableText(form.image_url),
                image_alt: cleanText(form.image_alt),
                image_badge_label: cleanText(form.image_badge_label),
                image_badge_text: cleanText(form.image_badge_text),
                opening_hours: form.opening_hours.map((item) => ({
                    day: item.day,
                    time: cleanText(item.time),
                })),
                updated_at: new Date().toISOString(),
            };

            const { data: existingData, error: existingError } = await supabase
                .from("contact_settings")
                .select("id")
                .eq("id", "main")
                .maybeSingle();

            if (existingError) throw existingError;

            if (existingData) {
                const { error } = await supabase
                    .from("contact_settings")
                    .update(payload)
                    .eq("id", "main");

                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from("contact_settings")
                    .insert(payload);

                if (error) throw error;
            }

            setSuccessMessage("Kontaktdaten wurden gespeichert.");
        } catch (error) {
            console.error("Contact settings save error:", error);
            setErrorMessage(
                error.message || "Kontaktdaten konnten nicht gespeichert werden."
            );
        } finally {
            setIsSubmitting(false);
        }
    }

    if (isLoading) {
        return (
            <Paper
                elevation={0}
                sx={{
                    py: 8,
                    px: 3,
                    borderRadius: 5,
                    backgroundColor: colors.paper,
                    border: `1px solid ${colors.border}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <CircularProgress sx={{ color: colors.brownDark }} />

                <Typography
                    sx={{
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontWeight: 800,
                        color: colors.muted,
                    }}
                >
                    Kontaktdaten werden geladen...
                </Typography>
            </Paper>
        );
    }

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    p: {
                        xs: 3,
                        md: 5,
                    },
                    borderRadius: {
                        xs: 4,
                        md: 6,
                    },
                    border: `1px solid ${colors.border}`,
                    background:
                        "radial-gradient(circle at 88% 14%, rgba(155,118,83,0.18) 0%, rgba(155,118,83,0) 30%), linear-gradient(145deg, rgba(255,250,242,0.96) 0%, rgba(247,241,232,0.92) 100%)",
                    boxShadow: "0 24px 80px rgba(78,54,30,0.1)",
                }}
            >
                <Typography
                    sx={{
                        mb: 1,
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontSize: 13,
                        fontWeight: 900,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: colors.brown,
                    }}
                >
                    CMS Einstellungen
                </Typography>

                <Typography
                    component="h1"
                    sx={{
                        mb: 1.5,
                        fontFamily: '"Quicksand", sans-serif',
                        fontSize: {
                            xs: "2.35rem",
                            md: "3.5rem",
                        },
                        lineHeight: 0.95,
                        fontWeight: 800,
                        letterSpacing: "-0.06em",
                        color: colors.ink,
                    }}
                >
                    Kontakt & Öffnungszeiten
                </Typography>

                <Typography
                    sx={{
                        maxWidth: 760,
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontSize: 16,
                        lineHeight: 1.75,
                        color: colors.muted,
                    }}
                >
                    Bearbeite hier die öffentlichen Kontaktinformationen der Website.
                </Typography>
            </Paper>

            {successMessage && (
                <Alert
                    severity="success"
                    sx={{
                        mb: 3,
                        borderRadius: 3,
                        fontFamily: '"Nunito Sans", sans-serif',
                        "& .MuiAlert-message": {
                            fontWeight: 700,
                        },
                    }}
                >
                    {successMessage}
                </Alert>
            )}

            {errorMessage && (
                <Alert
                    severity="error"
                    sx={{
                        mb: 3,
                        borderRadius: 3,
                        fontFamily: '"Nunito Sans", sans-serif',
                        "& .MuiAlert-message": {
                            fontWeight: 700,
                        },
                    }}
                >
                    {errorMessage}
                </Alert>
            )}

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        lg: "1fr 1fr",
                    },
                    gap: 3,
                }}
            >
                <Paper sx={cardStyles} elevation={0}>
                    <Typography sx={cardTitleStyles}>Texte</Typography>

                    <Stack spacing={2.2}>
                        <TextField
                            label="Label"
                            value={form.eyebrow}
                            onChange={(event) =>
                                updateField("eyebrow", event.target.value)
                            }
                            fullWidth
                            disabled={isSubmitting}
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Überschrift"
                            value={form.headline}
                            onChange={(event) =>
                                updateField("headline", event.target.value)
                            }
                            fullWidth
                            disabled={isSubmitting}
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Einleitung"
                            value={form.intro}
                            onChange={(event) =>
                                updateField("intro", event.target.value)
                            }
                            multiline
                            minRows={4}
                            fullWidth
                            disabled={isSubmitting}
                            sx={fieldStyles}
                        />
                    </Stack>
                </Paper>

                <Paper sx={cardStyles} elevation={0}>
                    <Typography sx={cardTitleStyles}>Kontakt</Typography>

                    <Stack spacing={2.2}>
                        <TextField
                            label="Geschäftsname"
                            value={form.business_name}
                            onChange={(event) =>
                                updateField(
                                    "business_name",
                                    event.target.value
                                )
                            }
                            fullWidth
                            disabled={isSubmitting}
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Ansprechpartner"
                            value={form.contact_person}
                            onChange={(event) =>
                                updateField(
                                    "contact_person",
                                    event.target.value
                                )
                            }
                            fullWidth
                            disabled={isSubmitting}
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Straße"
                            value={form.street}
                            onChange={(event) =>
                                updateField("street", event.target.value)
                            }
                            fullWidth
                            disabled={isSubmitting}
                            sx={fieldStyles}
                        />

                        <TextField
                            label="PLZ und Ort"
                            value={form.postal_city}
                            onChange={(event) =>
                                updateField("postal_city", event.target.value)
                            }
                            fullWidth
                            disabled={isSubmitting}
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Telefon Anzeige"
                            value={form.phone_label}
                            onChange={(event) =>
                                updateField("phone_label", event.target.value)
                            }
                            fullWidth
                            disabled={isSubmitting}
                            helperText="Beispiel: 04425 – 353"
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Telefon Link"
                            value={form.phone_href}
                            onChange={(event) =>
                                updateField("phone_href", event.target.value)
                            }
                            fullWidth
                            disabled={isSubmitting}
                            helperText="Beispiel: +494425353"
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Fax"
                            value={form.fax || ""}
                            onChange={(event) =>
                                updateField("fax", event.target.value)
                            }
                            fullWidth
                            disabled={isSubmitting}
                            sx={fieldStyles}
                        />

                        <TextField
                            label="E-Mail"
                            type="email"
                            value={form.email}
                            onChange={(event) =>
                                updateField("email", event.target.value)
                            }
                            fullWidth
                            disabled={isSubmitting}
                            sx={fieldStyles}
                        />
                    </Stack>
                </Paper>

                <Paper sx={cardStyles} elevation={0}>
                    <Typography sx={cardTitleStyles}>Bildbereich</Typography>

                    <Stack spacing={2.2}>
                        <TextField
                            label="Bild-URL"
                            value={form.image_url || ""}
                            onChange={(event) =>
                                updateField("image_url", event.target.value)
                            }
                            fullWidth
                            disabled={isSubmitting}
                            helperText="Leer lassen, wenn weiterhin das lokale Claudia-Bild genutzt werden soll."
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Bild Alt-Text"
                            value={form.image_alt}
                            onChange={(event) =>
                                updateField("image_alt", event.target.value)
                            }
                            fullWidth
                            disabled={isSubmitting}
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Bild-Badge Label"
                            value={form.image_badge_label}
                            onChange={(event) =>
                                updateField(
                                    "image_badge_label",
                                    event.target.value
                                )
                            }
                            fullWidth
                            disabled={isSubmitting}
                            sx={fieldStyles}
                        />

                        <TextField
                            label="Bild-Badge Text"
                            value={form.image_badge_text}
                            onChange={(event) =>
                                updateField(
                                    "image_badge_text",
                                    event.target.value
                                )
                            }
                            multiline
                            minRows={3}
                            fullWidth
                            disabled={isSubmitting}
                            sx={fieldStyles}
                        />
                    </Stack>
                </Paper>

                <Paper sx={cardStyles} elevation={0}>
                    <Typography sx={cardTitleStyles}>Öffnungszeiten</Typography>

                    <Stack spacing={2.2}>
                        {form.opening_hours.map((item, index) => (
                            <TextField
                                key={item.day}
                                label={item.day}
                                value={item.time}
                                onChange={(event) =>
                                    updateOpeningHour(
                                        index,
                                        event.target.value
                                    )
                                }
                                fullWidth
                                disabled={isSubmitting}
                                placeholder="z. B. 11:00 – 17:00 Uhr"
                                sx={fieldStyles}
                            />
                        ))}
                    </Stack>
                </Paper>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    mt: 3,
                    p: 2.5,
                    borderRadius: 5,
                    backgroundColor: colors.paper,
                    border: `1px solid ${colors.border}`,
                    display: "flex",
                    justifyContent: "flex-end",
                }}
            >
                <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                        minHeight: 52,
                        px: 3.5,
                        borderRadius: 999,
                        backgroundColor: colors.brownDark,
                        color: "#fff",
                        fontFamily: '"Nunito Sans", sans-serif',
                        fontSize: 14,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        boxShadow: "0 14px 30px rgba(111,81,54,0.22)",

                        "&:hover": {
                            backgroundColor: colors.brownDarker,
                            boxShadow:
                                "0 18px 38px rgba(111,81,54,0.28)",
                        },

                        "&:disabled": {
                            color: "rgba(255,255,255,0.78)",
                            backgroundColor: "rgba(111,81,54,0.62)",
                        },
                    }}
                >
                    {isSubmitting ? "Speichert..." : "Speichern"}
                </Button>
            </Paper>
        </Box>
    );
}

const cardStyles = {
    p: {
        xs: 3,
        md: 4,
    },
    borderRadius: 5,
    backgroundColor: colors.paper,
    border: `1px solid ${colors.border}`,
    boxShadow: "0 18px 60px rgba(78,54,30,0.08)",
};

const cardTitleStyles = {
    mb: 2.5,
    fontFamily: '"Quicksand", sans-serif',
    fontSize: {
        xs: "1.9rem",
        md: "2.3rem",
    },
    lineHeight: 1,
    fontWeight: 800,
    letterSpacing: "-0.055em",
    color: colors.ink,
};

const fieldStyles = {
    "& .MuiOutlinedInput-root": {
        borderRadius: 3,
        backgroundColor: "rgba(255,255,255,0.72)",
        fontFamily: '"Nunito Sans", sans-serif',

        "& fieldset": {
            borderColor: "rgba(42,38,33,0.14)",
        },

        "&:hover fieldset": {
            borderColor: "rgba(155,118,83,0.45)",
        },

        "&.Mui-focused fieldset": {
            borderColor: colors.brown,
            borderWidth: 2,
        },
    },

    "& .MuiInputLabel-root": {
        fontFamily: '"Nunito Sans", sans-serif',
        fontWeight: 700,
    },

    "& .MuiFormHelperText-root": {
        fontFamily: '"Nunito Sans", sans-serif',
    },
};

export default AdminContactSettings;